import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { InvitationPreview } from '@floorpoll/shared';
import { AuthLayout, FormError } from '../../components/Form.js';
import { ApiError, apiRequest } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';

/**
 * Story #8 — the landing page for an invitation link.
 *
 * The preview is public because the invitee may not have an account yet; the
 * accept call requires them to be signed in as the invited address.
 */
export function AcceptInvite() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { status, user, refreshMe } = useAuth();
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const result = await apiRequest<{ invitation: InvitationPreview }>(
          `/invitations/${encodeURIComponent(token)}`,
          { anonymous: true },
        );
        setPreview(result.invitation);
      } catch (caught) {
        setError(
          caught instanceof ApiError ? caught.message : 'That invitation could not be loaded.',
        );
      }
    })();
  }, [token]);

  const accept = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      await apiRequest(`/invitations/${encodeURIComponent(token)}/accept`, { method: 'POST' });
      await refreshMe();
      navigate('/polls');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not accept that invitation.');
    } finally {
      setBusy(false);
    }
  }, [token, refreshMe, navigate]);

  if (error && !preview) {
    return (
      <AuthLayout title="Invitation unavailable">
        <FormError message={error} />
        <p className="muted">
          <Link to="/signin">Go to sign in</Link>
        </p>
      </AuthLayout>
    );
  }

  if (!preview) {
    return (
      <AuthLayout title="Loading invitation…">
        <p className="muted">One moment.</p>
      </AuthLayout>
    );
  }

  const signedInAsInvitee =
    status === 'signed-in' && user?.email.toLowerCase() === preview.email.toLowerCase();

  return (
    <AuthLayout
      title={`Join ${preview.workspaceName}`}
      subtitle={`You've been invited as ${preview.role}.`}
    >
      <FormError message={error} />

      {signedInAsInvitee ? (
        <button type="button" className="block" disabled={busy} onClick={() => void accept()}>
          {busy ? 'Joining…' : `Join ${preview.workspaceName}`}
        </button>
      ) : status === 'signed-in' ? (
        <p className="muted">
          This invitation was sent to <strong>{preview.email}</strong>, but you are signed in as{' '}
          <strong>{user?.email}</strong>. Sign out and sign back in with the invited address to
          accept it.
        </p>
      ) : (
        <>
          <p className="muted">
            Sign in as <strong>{preview.email}</strong> to accept, or create an account with that
            address.
          </p>
          <Link
            className="button block"
            to={`/signin?next=${encodeURIComponent(`/invite/${token}`)}`}
          >
            Sign in
          </Link>
          <Link className="button secondary block" to="/signup">
            Create an account
          </Link>
        </>
      )}
    </AuthLayout>
  );
}
